import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { whatsappHref } from "@/lib/contact";

const PROJECTS = [
  {
    name: "Fairfox EON",
    sector: "Sector 140A · Noida Expressway",
    status: "Ready + New Phases",
    price: "₹8,000 – 9,500 /sq.ft · from ₹40 Lacs*",
    lease: "₹55 – 65 (offices)",
    product: "Offices · Retail · Food Court · Suites",
    standout: "Indicative entry pricing with buy, fit out & lease support",
    highlight: true,
  },
  {
    name: "Bhutani Alphathum",
    sector: "Sector 90",
    status: "Ready",
    price: "₹12,000 /sq.ft",
    lease: "~₹50 – 65",
    product: "Offices · Retail",
    standout: "Triple-tower campus with rooftop leisure deck",
  },
  {
    name: "ATS Bouquet",
    sector: "Sector 132",
    status: "Ready",
    price: "₹12,000 /sq.ft",
    lease: "~₹45 – 60",
    product: "Boutique Offices · Retail",
    standout: "Low-density boutique commercial format",
  },
  {
    name: "CRC The Flagship",
    sector: "Sector 140A",
    status: "Under Construction",
    price: "₹13,500 /sq.ft",
    lease: "On possession",
    product: "Offices · Retail",
    standout: "New-age campus, immediate neighbour to EON",
  },
  {
    name: "Express Trade Tower 2",
    sector: "Sector 132",
    status: "Ready",
    price: "₹12,000 /sq.ft",
    lease: "~₹60 – 75",
    product: "Grade-A Offices",
    standout: "Expressway-facing corporate tower",
  },
];

const waHref = whatsappHref(
  "Hi Ashish, please compare live options at EON vs Alphathum, ATS Bouquet, CRC Flagship and ETT2 for my budget."
);

export default function CompareProjects() {
  const [readyOnly, setReadyOnly] = useState(false);
  const rows = PROJECTS.filter((p) => !readyOnly || p.status.includes("Ready"));

  return (
    <section id="compare-projects" data-testid="compare-projects-section" className="py-20 sm:py-24 bg-[#0D0F12]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#D4AF37]">The Corridor, Compared</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6]">
            How EON <span className="gold-text italic">stacks up.</span>
          </h2>
          <p className="mt-5 max-w-xl text-sm sm:text-base text-[#9CA3AF] leading-relaxed">
            The Noida Expressway corridor has strong options. Here is an indicative view of the key distinctions.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex items-center gap-4"
        >
          <button
            data-testid="ready-only-toggle"
            onClick={() => setReadyOnly((v) => !v)}
            aria-pressed={readyOnly}
            className={`relative w-12 h-7 rounded-full transition-colors duration-300 ${readyOnly ? "bg-[#D4AF37]" : "bg-white/10 border border-white/15"}`}
          >
            <span
              className={`absolute top-1 w-5 h-5 rounded-full transition-all duration-300 ${
                readyOnly ? "left-6 bg-[#0B0C0E]" : "left-1 bg-[#9CA3AF]"
              }`}
            />
          </button>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">
            Ready to Move only
          </span>
          {readyOnly && (
            <span data-testid="ready-only-count" className="font-mono text-[11px] text-[#D4AF37]">
              {rows.length} of {PROJECTS.length} projects
            </span>
          )}
        </motion.div>

        <div
          data-testid="compare-projects-table"
          className="mt-6 overflow-hidden rounded-2xl border border-white/[0.07] bg-[#121418]"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-white/[0.07] text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">
                  <th className="px-6 py-5">Project</th>
                  <th className="px-6 py-5">Sector</th>
                  <th className="px-6 py-5">Status</th>
                  <th className="px-6 py-5">Price · Indicative</th>
                  <th className="px-6 py-5">Lease ₹/sq.ft · mo</th>
                  <th className="px-6 py-5">Product Mix</th>
                  <th className="px-6 py-5">Standout</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr
                    key={p.name}
                    data-testid={`compare-row-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`border-b border-white/[0.05] transition-colors ${
                      p.highlight
                        ? "bg-[#D4AF37]/[0.07] hover:bg-[#D4AF37]/[0.1]"
                        : "hover:bg-white/[0.02]"
                    }`}
                  >
                    <td className={`px-6 py-5 font-display text-lg font-semibold ${p.highlight ? "gold-text" : "text-[#F3F4F6]"}`}>
                      {p.name}
                      {p.highlight && (
                        <span className="block font-mono text-[9px] uppercase tracking-[0.25em] text-[#D4AF37] mt-1">Featured Project</span>
                      )}
                    </td>
                    <td className="px-6 py-5 text-[#D1D5DB]">{p.sector}</td>
                    <td className="px-6 py-5">
                      <span className={`px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.15em] border ${
                        p.status.includes("Ready")
                          ? "border-[#D4AF37]/40 text-[#F3E5AB]"
                          : "border-white/15 text-[#9CA3AF]"
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className={`px-6 py-5 font-mono text-xs ${p.highlight ? "text-[#F3E5AB] font-medium" : "text-[#9CA3AF]"}`}>
                      {p.price}
                    </td>
                    <td className={`px-6 py-5 font-mono text-xs ${p.highlight ? "text-[#F3E5AB]" : "text-[#9CA3AF]"}`}>
                      {p.lease}
                    </td>
                    <td className="px-6 py-5 text-[#9CA3AF]">{p.product}</td>
                    <td className="px-6 py-5 text-[#9CA3AF] leading-relaxed">{p.standout}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] p-7"
        >
          <p className="max-w-xl text-sm text-[#D1D5DB] leading-relaxed">
            As an independent investment desk, we advise across <span className="text-[#F3E5AB]">all of these projects</span> —
            not just EON. Tell us your budget and we will shortlist the strongest live options.
          </p>
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="compare-whatsapp-btn"
            className="shrink-0 px-6 py-3.5 bg-[#D4AF37] text-[#0B0C0E] font-sans text-xs font-semibold uppercase tracking-[0.18em] rounded-full hover:bg-[#F3E5AB] hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] transition-all duration-300 flex items-center gap-2"
          >
            <MessageCircle size={14} />
            Ask Ashish to Compare
          </a>
        </motion.div>

        <p className="mt-6 text-[11px] text-[#6B7280] leading-relaxed max-w-2xl">
          Prices and status reflect publicly listed information and market guidance, and change frequently —
          verify current rates, availability and inventory with the Homewiz.in advisory team before any decision.
        </p>
      </div>
    </section>
  );
}
